import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { WorkforceOperationsNavComponent } from './workforce-operations-nav.component';

@Component({
  selector: 'app-admin-compliance-fleet',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkforceOperationsNavComponent],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1500px] mx-auto">
        <div class="flex items-center justify-between gap-4 mb-5 shrink-0">
          <div>
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Workforce Operations</p>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Fleet Status</h1>
            <p class="text-slate-500 text-xs">Vehicle assignment, WOF/COF, RUC, odometer, and service readiness.</p>
          </div>
          <div class="flex items-center gap-2">
          <button type="button" (click)="loadFleet()" [disabled]="isLoading()" class="h-10 px-4 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold shadow-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2 cursor-pointer">
            <i class="ti" [ngClass]="isLoading() ? 'ti-loader animate-spin' : 'ti-refresh'"></i> Refresh
          </button>
          <button (click)="openCreateModal()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center gap-2 cursor-pointer" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
            <i class="ti ti-car text-base"></i> Add Vehicle
          </button>
          </div>
        </div>

        <app-workforce-operations-nav active="fleet"></app-workforce-operations-nav>

        <div class="mb-4 rounded-lg border px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3" [ngClass]="fleetReadinessBannerClass()">
          <div>
            <p class="text-[10px] font-bold uppercase tracking-[0.12em]">Fleet readiness</p>
            <p class="text-[13px] font-bold mt-0.5">{{ fleetReadinessLabel() }}</p>
            <p class="text-[11px] opacity-80 mt-1">Overdue compliance, RUC, service, and unassigned vehicles are listed first below.</p>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:min-w-[520px]">
            <div class="fleet-mini"><p>Attention</p><strong>{{ fleetAttention().length }}</strong></div>
            <div class="fleet-mini"><p>Overdue</p><strong>{{ overdueCount() }}</strong></div>
            <div class="fleet-mini"><p>Due soon</p><strong>{{ dueSoonCount() }}</strong></div>
            <div class="fleet-mini"><p>Unassigned</p><strong>{{ unassignedCount() }}</strong></div>
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Vehicles</p>
            <p class="text-xl font-semibold text-slate-900 mt-1">{{ fleet().length }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Active / Assigned</p>
            <p class="text-xl font-semibold text-[#1A6FA6] mt-1">{{ assignedCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Due Soon</p>
            <p class="text-xl font-semibold text-amber-700 mt-1">{{ dueSoonCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Overdue</p>
            <p class="text-xl font-semibold text-rose-700 mt-1">{{ overdueCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Unassigned</p>
            <p class="text-xl font-semibold text-slate-700 mt-1">{{ unassignedCount() }}</p>
          </div>
        </div>

        @if (fleetAttention().length) {
          <section class="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden mb-5">
            <div class="px-4 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between gap-3">
              <div>
                <h2 class="text-[12px] font-bold text-amber-900">Fleet attention queue</h2>
                <p class="text-[10px] text-amber-700 mt-0.5">Vehicles needing assignment, compliance, RUC, or service action.</p>
              </div>
              <span class="px-2 py-1 rounded-md bg-white text-amber-700 text-[10px] font-bold">{{ fleetAttention().length }}</span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
              @for (vehicle of fleetAttention().slice(0, 6); track vehicle.id) {
                <button type="button" (click)="viewVehicle(vehicle)" class="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2 text-left hover:bg-amber-50 transition">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-[12px] font-bold text-slate-900 truncate">{{ vehicle.rego }} · {{ vehicle.vehicle }}</p>
                      <p class="text-[10px] text-slate-500 truncate">{{ vehicle.name || 'Unassigned' }}</p>
                    </div>
                    <span class="px-2 py-0.5 rounded-md bg-white border border-amber-100 text-[9px] font-bold text-amber-700">{{ vehicleReadinessLabel(vehicle) }}</span>
                  </div>
                  <p class="mt-1 text-[10px] text-slate-600 truncate">{{ vehicleIssues(vehicle).join(' · ') }}</p>
                </button>
              }
            </div>
          </section>
        }

        <div class="bg-white rounded-xl shadow-sm overflow-hidden border border-[#E7EEF5]">
          <div class="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-slate-100">
            <div class="relative flex-1 min-w-[220px] max-w-sm">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]"></i>
              <input type="text" placeholder="Search vehicle, plate, assignee, RUC, location..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] placeholder:text-slate-400">
            </div>
            <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
              <option value="">All Vehicles</option>
              <option value="attention">Needs Attention</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <option value="available">Available</option>
              <option value="due">Due Soon</option>
              <option value="overdue">Overdue</option>
              <option value="ruc">RUC Due</option>
              <option value="service">Service Due</option>
            </select>
            <div class="flex flex-wrap gap-1.5">
              @for (filter of quickFilters; track filter.value) {
                <button type="button" (click)="statusFilter.set(filter.value)" class="h-8 px-2.5 rounded-md border text-[10px] font-bold" [ngClass]="statusFilter() === filter.value ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:text-[#1A6FA6]'">{{ filter.label }}</button>
              }
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50">
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Vehicle</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assignment</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Compliance</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">RUC</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Odometer</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Next Service</th>
                  <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                @if (isLoading()) {
                  <tr>
                    <td colspan="7" class="px-4 py-12 text-center text-sm text-slate-400">
                      <i class="ti ti-loader animate-spin mr-2"></i> Loading fleet vehicles...
                    </td>
                  </tr>
                } @else {
                @for (vehicle of filteredFleet(); track vehicle.id) {
                  <tr class="hover:bg-slate-50 transition-colors border-t border-slate-100 cursor-pointer" (click)="viewVehicle(vehicle)">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-3">
                        <div class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center shrink-0">
                          <i class="ti ti-truck text-lg"></i>
                        </div>
                        <div>
                          <p class="font-semibold text-slate-900 text-sm">{{ vehicle.rego }}</p>
                          <p class="text-[10px] text-slate-400">{{ vehicle.vehicle || vehicle.id }}</p>
                          <span class="inline-flex mt-1 px-2 py-0.5 rounded-md border text-[9px] font-bold uppercase" [ngClass]="vehicleReadinessClass(vehicle)">{{ vehicleReadinessLabel(vehicle) }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-800 text-xs">{{ vehicle.name || 'Unassigned' }}</p>
                      <p class="text-[10px] text-slate-400 max-w-[260px] truncate">{{ vehicle.job || '-' }} - {{ vehicle.location || 'HQ' }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="expiryBadgeClass(vehicle.wof)">
                        {{ vehicle.complianceType || 'WOF' }} {{ displayDate(vehicle.wof) }}
                      </span>
                      <p class="text-[10px] text-slate-400 mt-1">{{ daysText(vehicle.wof) }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border" [ngClass]="rucBadgeClass(vehicle)">{{ rucStatus(vehicle) }}</span>
                      <p class="text-[10px] text-slate-500 mt-1">{{ vehicle.ruc || 'Not recorded' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs font-semibold text-slate-700">{{ vehicle.odo }}</td>
                    <td class="px-4 py-3">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border" [ngClass]="serviceBadgeClass(vehicle)">{{ serviceStatus(vehicle) }}</span>
                      <p class="text-[10px] text-slate-500 mt-1">{{ vehicle.service }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex justify-end gap-2">
                        <button type="button" (click)="openOdometerModal(vehicle); $event.stopPropagation()" class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer">
                          Odometer
                        </button>
                        <button type="button" (click)="openAssignmentModal(vehicle); $event.stopPropagation()" class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer">
                          Assign
                        </button>
                        <button type="button" (click)="openEditModal(vehicle); $event.stopPropagation()" class="h-8 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold hover:bg-[#D5EBF6] transition-colors cursor-pointer">
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" class="px-4 py-12 text-center text-sm text-slate-400">
                      No vehicles match the current filters.
                    </td>
                  </tr>
                }
                }
              </tbody>
            </table>
          </div>
        </div>

        @if (showCreateModal() || editingVehicle()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">{{ editingVehicle() ? 'Edit Vehicle' : 'Add Vehicle' }}</h2>
                  <p class="text-xs text-slate-500 mt-1">Record vehicle, compliance, RUC, assignment, and service information.</p>
                </div>
                <button type="button" (click)="closeVehicleForm()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer">
                  <i class="ti ti-x text-lg"></i>
                </button>
              </div>

              <form #vehicleForm="ngForm" (ngSubmit)="saveVehicle(vehicleForm)" class="flex flex-col overflow-hidden">
                <div class="p-5 overflow-y-auto space-y-5">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Vehicle</span>
                      <input type="text" name="vehicle" [(ngModel)]="formModel.vehicle" required class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Registration</span>
                      <input type="text" name="rego" [(ngModel)]="formModel.rego" required class="field-input uppercase">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Assigned To</span>
                      <select name="assigned_user_id" [(ngModel)]="formModel.assigned_user_id" class="field-input bg-white">
                        <option [ngValue]="null">Unassigned</option>
                        @for (worker of workerOptions(); track worker.id) {
                          <option [ngValue]="worker.id">{{ worker.name }}</option>
                        }
                      </select>
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Status</span>
                      <select name="status" [(ngModel)]="formModel.status" class="field-input bg-white">
                        <option value="Available">Available</option>
                        <option value="Active on Job">Active on Job</option>
                        <option value="Offline">Offline</option>
                      </select>
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Current Job</span>
                      <input type="text" name="job" [(ngModel)]="formModel.job" class="field-input">
                    </label>
                    <label class="block md:col-span-2">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Location</span>
                      <input type="text" name="location" [(ngModel)]="formModel.location" class="field-input">
                    </label>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Compliance Type</span>
                      <select name="complianceType" [(ngModel)]="formModel.complianceType" class="field-input bg-white">
                        <option value="WOF">WOF</option>
                        <option value="COF">COF</option>
                      </select>
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Compliance Expiry</span>
                      <input type="date" name="wof" [(ngModel)]="formModel.wof" required class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">RUC Paid To</span>
                      <input type="text" name="ruc" [(ngModel)]="formModel.ruc" class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Odometer</span>
                      <input type="text" name="odo" [(ngModel)]="formModel.odo" required class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Next Service</span>
                      <input type="text" name="service" [(ngModel)]="formModel.service" required class="field-input">
                    </label>
                  </div>
                </div>

                <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                  <button type="button" (click)="closeVehicleForm()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 cursor-pointer">Cancel</button>
                  <button type="submit" [disabled]="!vehicleForm.valid || isSaving()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer inline-flex items-center gap-2">
                    @if (isSaving()) {
                      <i class="ti ti-loader animate-spin"></i> Saving...
                    } @else {
                      {{ editingVehicle() ? 'Save Changes' : 'Save Vehicle' }}
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        }

        @if (selectedVehicle()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Vehicle Details</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ selectedVehicle()?.id }}</p>
                </div>
                <div class="flex gap-2">
                  <button type="button" (click)="openOdometerModal(selectedVehicle()!)" class="px-4 py-2 bg-white text-slate-700 border border-slate-200 text-xs font-semibold rounded-lg hover:bg-slate-50 cursor-pointer">
                    <i class="ti ti-gauge"></i> Odometer
                  </button>
                  <button type="button" (click)="openAssignmentModal(selectedVehicle()!)" class="px-4 py-2 bg-white text-slate-700 border border-slate-200 text-xs font-semibold rounded-lg hover:bg-slate-50 cursor-pointer">
                    <i class="ti ti-user-check"></i> Assign
                  </button>
                  <button type="button" (click)="deleteVehicle(selectedVehicle()!)" class="px-4 py-2 bg-rose-50 text-rose-700 border border-rose-100 text-xs font-semibold rounded-lg hover:bg-rose-100 cursor-pointer">
                    <i class="ti ti-trash"></i> Remove
                  </button>
                  <button type="button" (click)="openEditModal(selectedVehicle()!)" class="px-4 py-2 bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6] text-xs font-semibold rounded-lg hover:bg-[#D5EBF6] cursor-pointer">
                    <i class="ti ti-pencil"></i> Edit
                  </button>
                  <button type="button" (click)="selectedVehicle.set(null)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer">
                    <i class="ti ti-x text-lg"></i>
                  </button>
                </div>
              </div>

              <div class="p-5 overflow-y-auto">
                <div class="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
                  <aside class="bg-slate-50 rounded-xl border border-slate-200 p-4">
                    <div class="w-16 h-16 rounded-xl bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center text-3xl mb-4">
                      <i class="ti ti-truck"></i>
                    </div>
                    <h3 class="text-2xl font-semibold text-slate-950">{{ selectedVehicle()?.rego }}</h3>
                    <p class="text-sm font-semibold text-[#1A6FA6] mt-1">{{ selectedVehicle()?.vehicle || 'Vehicle' }}</p>
                    <p class="text-xs font-semibold text-slate-500 mt-1">{{ selectedVehicle()?.status }}</p>
                    <div class="mt-4 flex flex-wrap gap-2">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="expiryBadgeClass(selectedVehicle()?.wof || '')">{{ selectedVehicle()?.complianceType || 'WOF' }} {{ expiryStatus(selectedVehicle()?.wof || '') }}</span>
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="serviceBadgeClass(selectedVehicle())">Service {{ serviceStatus(selectedVehicle()) }}</span>
                    </div>
                  </aside>

                  <section class="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div class="detail-panel">
                      <p class="panel-title">Assignment</p>
                      <p><span>Driver</span>{{ selectedVehicle()?.name || 'Unassigned' }}</p>
                      <p><span>Job</span>{{ selectedVehicle()?.job || '-' }}</p>
                      <p><span>Location</span>{{ selectedVehicle()?.location || 'HQ' }}</p>
                      <p><span>Status</span>{{ selectedVehicle()?.status || 'Available' }}</p>
                    </div>
                    <div class="detail-panel">
                      <p class="panel-title">Compliance</p>
                      <p><span>{{ selectedVehicle()?.complianceType || 'WOF' }}</span>{{ displayDate(selectedVehicle()?.wof || '') }} ({{ daysText(selectedVehicle()?.wof || '') }})</p>
                      <p><span>RUC</span>{{ selectedVehicle()?.ruc || 'Not recorded' }}</p>
                    </div>
                    <div class="detail-panel xl:col-span-2">
                      <p class="panel-title">Maintenance</p>
                      <p><span>Odometer</span>{{ selectedVehicle()?.odo }}</p>
                      <p><span>Next Service</span>{{ selectedVehicle()?.service }}</p>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        }

        @if (odometerVehicle()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Update Odometer</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ odometerVehicle()?.rego }} - {{ odometerVehicle()?.vehicle }}</p>
                </div>
                <button type="button" (click)="closeOdometerModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer">
                  <i class="ti ti-x text-lg"></i>
                </button>
              </div>
              <form #odometerNgForm="ngForm" (ngSubmit)="saveOdometer(odometerNgForm)">
                <div class="p-5 space-y-4">
                  <div class="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600">
                    Current reading: <strong class="text-slate-900">{{ odometerVehicle()?.odo || 'Not recorded' }}</strong>
                  </div>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">New Odometer Reading</span>
                    <input type="number" name="odometer_km" [(ngModel)]="odometerForm.odometer_km" min="0" required class="field-input">
                  </label>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span>
                    <textarea name="notes" [(ngModel)]="odometerForm.notes" rows="3" class="field-input h-auto py-2 resize-none" placeholder="Optional reading source or reason"></textarea>
                  </label>
                </div>
                <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                  <button type="button" (click)="closeOdometerModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 cursor-pointer">Cancel</button>
                  <button type="submit" [disabled]="!odometerNgForm.valid || isSavingOdometer()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 cursor-pointer inline-flex items-center gap-2">
                    @if (isSavingOdometer()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { Save Reading }
                  </button>
                </div>
              </form>
            </div>
          </div>
        }

        @if (assignmentVehicle()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Update Assignment</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ assignmentVehicle()?.rego }} - {{ assignmentVehicle()?.vehicle }}</p>
                </div>
                <button type="button" (click)="closeAssignmentModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer">
                  <i class="ti ti-x text-lg"></i>
                </button>
              </div>
              <form #assignmentNgForm="ngForm" (ngSubmit)="saveAssignment(assignmentNgForm)">
                <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Assigned To</span>
                    <select name="assigned_user_id" [(ngModel)]="assignmentForm.assigned_user_id" class="field-input bg-white">
                      <option [ngValue]="null">Unassigned</option>
                      @for (worker of workerOptions(); track worker.id) {
                        <option [ngValue]="worker.id">{{ worker.name }}</option>
                      }
                    </select>
                  </label>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Status</span>
                    <select name="status" [(ngModel)]="assignmentForm.status" class="field-input bg-white">
                      <option value="Available">Available</option>
                      <option value="Active on Job">Active on Job</option>
                      <option value="Offline">Offline</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Current Job</span>
                    <input type="text" name="job" [(ngModel)]="assignmentForm.job" class="field-input">
                  </label>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Location</span>
                    <input type="text" name="location" [(ngModel)]="assignmentForm.location" class="field-input">
                  </label>
                  <label class="block md:col-span-2">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span>
                    <textarea name="notes" [(ngModel)]="assignmentForm.notes" rows="3" class="field-input h-auto py-2 resize-none" placeholder="Optional dispatch note"></textarea>
                  </label>
                </div>
                <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                  <button type="button" (click)="closeAssignmentModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 cursor-pointer">Cancel</button>
                  <button type="submit" [disabled]="isSavingAssignment()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 cursor-pointer inline-flex items-center gap-2">
                    @if (isSavingAssignment()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { Save Assignment }
                  </button>
                </div>
              </form>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 0.75rem;
      font-size: 0.875rem;
      color: #1e293b;
      outline: none;
      transition: border-color 160ms ease, background 160ms ease;
    }
    .field-input:focus {
      border-color: #1A6FA6;
      background: #fff;
    }
    .detail-panel {
      border: 1px solid #e2e8f0;
      border-radius: 0.75rem;
      background: #fff;
      padding: 1rem;
    }
    .panel-title {
      margin-bottom: 0.75rem;
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #94a3b8;
    }
    .detail-panel p:not(.panel-title) {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.45rem 0;
      border-bottom: 1px solid #f1f5f9;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #334155;
    }
    .detail-panel p:last-child {
      border-bottom: 0;
    }
    .detail-panel span {
      color: #94a3b8;
      font-weight: 700;
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
  `]
})
export class AdminComplianceFleetComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  fleet = signal<any[]>([]);
  workerOptions = signal<Array<{ id: number; name: string }>>([]);
  isLoading = signal(true);
  isSaving = signal(false);
  isSavingOdometer = signal(false);
  isSavingAssignment = signal(false);
  searchQuery = signal('');
  statusFilter = signal('');
  showCreateModal = signal(false);
  selectedVehicle = signal<any | null>(null);
  editingVehicle = signal<any | null>(null);
  odometerVehicle = signal<any | null>(null);
  assignmentVehicle = signal<any | null>(null);

  formModel: any = this.emptyVehicleForm();
  odometerForm: any = { id: null, odometer_km: '', notes: '' };
  assignmentForm: any = { id: null, assigned_user_id: null, name: '', status: 'Available', job: '-', location: 'HQ', notes: '' };
  quickFilters = [
    { label: 'All', value: '' },
    { label: 'Attention', value: 'attention' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'RUC', value: 'ruc' },
    { label: 'Service', value: 'service' },
    { label: 'Unassigned', value: 'unassigned' }
  ];

  ngOnInit() {
    this.loadAssignableWorkers();
    this.loadFleet();
    this.refreshTimer = setInterval(() => this.loadFleet(false), 30000);
  }

  ngOnDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  loadFleet(showLoading = true) {
    if (showLoading) this.isLoading.set(true);
    this.api.getFleetVehicles().subscribe({
      next: (res: any) => {
        if (res.success) {
          this.fleet.set((res.data?.vehicles || []).map((vehicle: any) => this.normaliseVehicle(vehicle)));
        } else {
          showToast('error', res.message || 'Failed to load fleet vehicles.');
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Network error loading fleet vehicles.');
      }
    });
  }

  loadAssignableWorkers() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        if (!res.success) return;
        const workers = (res.data?.users || [])
          .filter((worker: any) => Number(worker.is_active) === 1 && !!worker.is_activated)
          .map((worker: any) => ({
            id: Number(worker.id),
            name: String(worker.name || '').trim()
          }))
          .filter((worker: any) => worker.id > 0 && worker.name)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.workerOptions.set(workers);
      }
    });
  }

  filteredFleet = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();

    return this.fleet().filter(vehicle => {
      const queryMatches = !query || [
        vehicle.rego,
        vehicle.vehicle,
        vehicle.name,
        vehicle.job,
        vehicle.location,
        vehicle.status,
        vehicle.ruc
      ].some(value => String(value || '').toLowerCase().includes(query));

      const isAssigned = this.isAssigned(vehicle);
      const hasDue = this.isDueSoon(vehicle.wof) || this.isServiceDueSoon(vehicle) || this.isRucDueSoon(vehicle);
      const hasOverdue = this.isOverdue(vehicle.wof) || this.isServiceOverdue(vehicle) || this.isRucOverdue(vehicle);
      const needsAttention = !isAssigned || hasDue || hasOverdue;
      const statusMatches = !status
        || (status === 'attention' && needsAttention)
        || (status === 'assigned' && isAssigned)
        || (status === 'unassigned' && !isAssigned)
        || (status === 'available' && vehicle.status === 'Available')
        || (status === 'due' && hasDue)
        || (status === 'overdue' && hasOverdue)
        || (status === 'ruc' && (this.isRucDueSoon(vehicle) || this.isRucOverdue(vehicle)))
        || (status === 'service' && (this.isServiceDueSoon(vehicle) || this.isServiceOverdue(vehicle)));

      return queryMatches && statusMatches;
    });
  });

  assignedCount = computed(() => this.fleet().filter(vehicle => this.isAssigned(vehicle)).length);
  unassignedCount = computed(() => this.fleet().filter(vehicle => !this.isAssigned(vehicle)).length);
  dueSoonCount = computed(() => this.fleet().filter(vehicle => this.isDueSoon(vehicle.wof) || this.isServiceDueSoon(vehicle) || this.isRucDueSoon(vehicle)).length);
  overdueCount = computed(() => this.fleet().filter(vehicle => this.isOverdue(vehicle.wof) || this.isServiceOverdue(vehicle) || this.isRucOverdue(vehicle)).length);
  fleetAttention = computed(() => this.fleet()
    .filter(vehicle => this.vehicleIssues(vehicle).length > 0)
    .sort((a, b) => this.vehiclePriority(a) - this.vehiclePriority(b)));

  openCreateModal() {
    this.formModel = this.emptyVehicleForm();
    this.showCreateModal.set(true);
  }

  openEditModal(vehicle: any) {
    this.selectedVehicle.set(null);
    this.editingVehicle.set(vehicle);
    this.formModel = { ...vehicle, assigned_user_id: vehicle.assigned_user_id ?? null };
  }

  closeVehicleForm() {
    this.showCreateModal.set(false);
    this.editingVehicle.set(null);
    this.formModel = this.emptyVehicleForm();
  }

  viewVehicle(vehicle: any) {
    this.selectedVehicle.set(vehicle);
  }

  openOdometerModal(vehicle: any) {
    this.odometerVehicle.set(vehicle);
    this.odometerForm = {
      id: vehicle.id,
      odometer_km: vehicle.odometer_km || this.kmNumber(vehicle.odo) || '',
      notes: ''
    };
  }

  closeOdometerModal() {
    this.odometerVehicle.set(null);
    this.odometerForm = { id: null, odometer_km: '', notes: '' };
  }

  openAssignmentModal(vehicle: any) {
    this.assignmentVehicle.set(vehicle);
    this.assignmentForm = {
      id: vehicle.id,
      assigned_user_id: vehicle.assigned_user_id ?? null,
      name: vehicle.name || 'Unassigned',
      status: vehicle.status || 'Available',
      job: vehicle.job || '-',
      location: vehicle.location || 'HQ',
      notes: ''
    };
  }

  closeAssignmentModal() {
    this.assignmentVehicle.set(null);
    this.assignmentForm = { id: null, assigned_user_id: null, name: '', status: 'Available', job: '-', location: 'HQ', notes: '' };
  }

  saveVehicle(form: NgForm) {
    if (!form.valid || this.isSaving()) return;

    const model = {
      ...this.formModel,
      vehicle: String(this.formModel.vehicle || '').trim(),
      rego: String(this.formModel.rego || '').trim().toUpperCase(),
      assigned_user_id: this.formModel.assigned_user_id || null,
      name: this.workerName(this.formModel.assigned_user_id),
      job: String(this.formModel.job || '').trim() || '-',
      location: String(this.formModel.location || '').trim() || 'HQ',
      complianceType: this.formModel.complianceType || 'WOF',
      ruc: String(this.formModel.ruc || '').trim() || 'Not recorded'
    };

    this.isSaving.set(true);
    const request = this.editingVehicle()
      ? this.api.updateFleetVehicle(model)
      : this.api.createFleetVehicle(model);

    request.subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', this.editingVehicle() ? 'Vehicle updated.' : 'Vehicle added.');
          this.closeVehicleForm();
          this.loadFleet(false);
        } else {
          showToast('error', res.message || 'Failed to save vehicle.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error saving vehicle.');
      }
    });
  }

  deleteVehicle(vehicle: any) {
    if (!vehicle?.id) return;
    if (!confirm(`Remove ${vehicle.rego} from fleet?`)) return;
    this.api.deleteFleetVehicle(Number(vehicle.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Vehicle removed.');
          this.selectedVehicle.set(null);
          this.loadFleet(false);
        } else {
          showToast('error', res.message || 'Failed to remove vehicle.');
        }
      },
      error: () => showToast('error', 'Network error removing vehicle.')
    });
  }

  saveOdometer(form: NgForm) {
    if (!form.valid || this.isSavingOdometer()) return;
    const current = this.kmNumber(this.odometerVehicle()?.odo);
    const next = this.kmNumber(this.odometerForm.odometer_km);
    if (next === null) {
      showToast('error', 'Enter a valid odometer reading.');
      return;
    }
    if (current !== null && next < current) {
      showToast('error', 'New odometer reading cannot be lower than the current reading.');
      return;
    }

    this.isSavingOdometer.set(true);
    this.api.updateFleetOdometer({
      id: this.odometerForm.id,
      odometer_km: next,
      notes: this.odometerForm.notes || ''
    }).subscribe({
      next: (res: any) => {
        this.isSavingOdometer.set(false);
        if (res.success) {
          showToast('success', 'Odometer updated.');
          this.closeOdometerModal();
          this.selectedVehicle.set(null);
          this.loadFleet(false);
        } else {
          showToast('error', res.message || 'Failed to update odometer.');
        }
      },
      error: (err) => {
        this.isSavingOdometer.set(false);
        showToast('error', err?.error?.message || 'Network error updating odometer.');
      }
    });
  }

  saveAssignment(form: NgForm) {
    if (!form.valid || this.isSavingAssignment()) return;
    this.isSavingAssignment.set(true);
    this.api.assignFleetVehicle({
      id: this.assignmentForm.id,
      assigned_user_id: this.assignmentForm.assigned_user_id || null,
      name: this.workerName(this.assignmentForm.assigned_user_id),
      status: this.assignmentForm.status || 'Available',
      job: String(this.assignmentForm.job || '').trim() || '-',
      location: String(this.assignmentForm.location || '').trim() || 'HQ',
      notes: this.assignmentForm.notes || ''
    }).subscribe({
      next: (res: any) => {
        this.isSavingAssignment.set(false);
        if (res.success) {
          showToast('success', 'Assignment updated.');
          this.closeAssignmentModal();
          this.selectedVehicle.set(null);
          this.loadFleet(false);
        } else {
          showToast('error', res.message || 'Failed to update assignment.');
        }
      },
      error: (err) => {
        this.isSavingAssignment.set(false);
        showToast('error', err?.error?.message || 'Network error updating assignment.');
      }
    });
  }

  displayDate(value: string): string {
    const date = this.parseDate(value);
    if (!date) return value || 'Not recorded';
    return date.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  expiryStatus(value: string): string {
    if (this.isOverdue(value)) return 'Overdue';
    if (this.isDueSoon(value)) return 'Due Soon';
    return 'Current';
  }

  expiryBadgeClass(value: string): string {
    if (this.isOverdue(value)) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (this.isDueSoon(value)) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  serviceStatus(vehicle: any | null): string {
    if (!vehicle) return 'Not Recorded';
    if (this.isServiceOverdue(vehicle)) return 'Overdue';
    if (this.isServiceDueSoon(vehicle)) return 'Due Soon';
    return 'Current';
  }

  serviceBadgeClass(vehicle: any | null): string {
    if (!vehicle) return 'bg-slate-50 text-slate-600 border-slate-200';
    if (this.isServiceOverdue(vehicle)) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (this.isServiceDueSoon(vehicle)) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  rucStatus(vehicle: any | null): string {
    if (!vehicle || this.kmNumber(vehicle?.ruc) === null) return 'Not Recorded';
    if (this.isRucOverdue(vehicle)) return 'Overdue';
    if (this.isRucDueSoon(vehicle)) return 'Due Soon';
    return 'Current';
  }

  rucBadgeClass(vehicle: any | null): string {
    if (!vehicle || this.kmNumber(vehicle?.ruc) === null) return 'bg-slate-50 text-slate-600 border-slate-200';
    if (this.isRucOverdue(vehicle)) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (this.isRucDueSoon(vehicle)) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  vehicleIssues(vehicle: any): string[] {
    const issues: string[] = [];
    if (!this.isAssigned(vehicle)) issues.push('Unassigned');
    if (this.isOverdue(vehicle.wof)) issues.push(`${vehicle.complianceType || 'WOF'} overdue`);
    else if (this.isDueSoon(vehicle.wof)) issues.push(`${vehicle.complianceType || 'WOF'} due soon`);
    if (this.isRucOverdue(vehicle)) issues.push('RUC overdue');
    else if (this.isRucDueSoon(vehicle)) issues.push('RUC due soon');
    if (this.isServiceOverdue(vehicle)) issues.push('Service overdue');
    else if (this.isServiceDueSoon(vehicle)) issues.push('Service due soon');
    return issues;
  }

  vehicleReadinessLabel(vehicle: any): string {
    if (this.isOverdue(vehicle.wof) || this.isRucOverdue(vehicle) || this.isServiceOverdue(vehicle)) return 'Overdue';
    if (this.vehicleIssues(vehicle).length > 0) return 'Needs Attention';
    return 'Ready';
  }

  vehicleReadinessClass(vehicle: any): string {
    const label = this.vehicleReadinessLabel(vehicle);
    if (label === 'Overdue') return 'bg-rose-50 text-rose-700 border-rose-100';
    if (label === 'Needs Attention') return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  fleetReadinessLabel(): string {
    if (this.overdueCount() > 0) return `${this.overdueCount()} vehicle${this.overdueCount() === 1 ? '' : 's'} overdue`;
    if (this.fleetAttention().length > 0) return `${this.fleetAttention().length} vehicle${this.fleetAttention().length === 1 ? '' : 's'} need attention`;
    return 'Fleet is ready';
  }

  fleetReadinessBannerClass(): string {
    if (this.overdueCount() > 0) return 'bg-rose-50 border-rose-100 text-rose-900';
    if (this.fleetAttention().length > 0) return 'bg-amber-50 border-amber-100 text-amber-900';
    return 'bg-emerald-50 border-emerald-100 text-emerald-900';
  }

  daysText(value: string): string {
    const days = this.daysUntil(value);
    if (days === null) return 'date not recorded';
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'due today';
    return `${days} days remaining`;
  }

  private isDueSoon(value: string): boolean {
    const days = this.daysUntil(value);
    return days !== null && days >= 0 && days <= 60;
  }

  private isOverdue(value: string): boolean {
    const days = this.daysUntil(value);
    return days !== null && days < 0;
  }

  private isServiceDueSoon(vehicle: any): boolean {
    const remaining = this.kmRemaining(vehicle?.odo, vehicle?.service);
    return remaining !== null && remaining >= 0 && remaining <= 2000;
  }

  private isServiceOverdue(vehicle: any): boolean {
    const remaining = this.kmRemaining(vehicle?.odo, vehicle?.service);
    return remaining !== null && remaining < 0;
  }

  private isRucDueSoon(vehicle: any): boolean {
    const remaining = this.kmRemaining(vehicle?.odo, vehicle?.ruc);
    return remaining !== null && remaining >= 0 && remaining <= 1000;
  }

  private isRucOverdue(vehicle: any): boolean {
    const remaining = this.kmRemaining(vehicle?.odo, vehicle?.ruc);
    return remaining !== null && remaining < 0;
  }

  private vehiclePriority(vehicle: any): number {
    if (this.isOverdue(vehicle.wof) || this.isRucOverdue(vehicle) || this.isServiceOverdue(vehicle)) return 1;
    if (!this.isAssigned(vehicle)) return 2;
    if (this.isDueSoon(vehicle.wof) || this.isRucDueSoon(vehicle) || this.isServiceDueSoon(vehicle)) return 3;
    return 4;
  }

  private kmRemaining(currentValue: string, targetValue: string): number | null {
    const current = this.kmNumber(currentValue);
    const target = this.kmNumber(targetValue);
    if (current === null || target === null) return null;
    return target - current;
  }

  private kmNumber(value: string): number | null {
    const numeric = String(value || '').replace(/[^0-9]/g, '');
    if (!numeric) return null;
    return Number(numeric);
  }

  private daysUntil(value: string): number | null {
    const target = this.parseDate(value);
    if (!target) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  }

  private parseDate(value: string): Date | null {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toDateInput(value: string): string {
    const date = this.parseDate(value);
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normaliseVehicle(vehicle: any): any {
    return {
      ...vehicle,
      assigned_user_id: vehicle.assigned_user_id ?? null,
      wof: this.toDateInput(vehicle.wof),
      vehicle: vehicle.vehicle || 'Vehicle',
      complianceType: vehicle.complianceType || 'WOF',
      ruc: vehicle.ruc || 'Not recorded',
      status: vehicle.status || 'Available',
      job: vehicle.job || '-',
      location: vehicle.location || 'HQ'
    };
  }

  private emptyVehicleForm(): any {
    return {
      id: '',
      vehicle: '',
      rego: '',
      assigned_user_id: null,
      name: '',
      status: 'Available',
      job: '-',
      location: 'HQ',
      odo: '',
      wof: '',
      complianceType: 'WOF',
      ruc: '',
      service: ''
    };
  }

  private isAssigned(vehicle: any): boolean {
    return !!vehicle?.assigned_user_id
      || (vehicle?.name && vehicle.name !== 'Unassigned')
      || vehicle?.status === 'Active on Job'
      || (!!vehicle?.job && vehicle.job !== '-');
  }

  private workerName(workerId: number | string | null | undefined): string {
    const id = Number(workerId);
    if (!id) return 'Unassigned';
    return this.workerOptions().find(worker => worker.id === id)?.name || 'Unassigned';
  }
}
